import DataReportBlock from '../../reporter/report/DataReportBlock';
import LocalState, {
  ReporterState,
  updateLocalState,
  PlayState
} from '../../state/LocalState';
import PendingReport from './report/PendingReport';
import ContentConfig from '../../config/ContentConfig';
import isValidGuid from '../../util/isValidGuid';
import logger, { ReporterAppender } from '../../log';
import autobind from '../../autobind';
import formatReportRequest, {
  FormatReportRequest,
  KsdkRequest
} from './formatReportRequest';
import isaFailedRequest from './isaFailedRequest';
import { ReporterStoredInfo } from './storeReportInfo';
import { getStoredReportInfo, saveStoredReportInfo } from './storeReportInfo';
import processReport from './processReport';
import mergeReport from './mergeReport';
import { hasSendBeacon } from '../utils/navigator';
import getKollectiveHeaders from '../utils/getKollectiveHeaders';
import isPlayerEventHandled from './events/isPlayerEventHandled';
import maybeUpdate11AgentData from '../../ecdn11/maybeUpdate11AgentData';
import setMergedReportIndices from '../../reporter/setMergedReportIndices';
import timeSinceStart from '../../util/timeSinceStart';
import updateReporterWithContent from './updateReporterWithContent';
import { difference, mergedCopy } from '../utils/objects';
import {
  Ecdn11Block,
  MaybeGeneralBlock,
  Report
} from '../../reporter/report/Report';
import {
  CONDITION_EXPEDITE,
  NonStdPlayerEvents,
  StdPlayerEvents,
  ReporterSections
} from './constants';
import SignalConnection from '../../webrtc/agent/SignalBus';
import reporterIsReadyToStart, {
  hasMinimalRequiredState,
  hasTimedOut
} from './reporterIsReadyToStart';
import sendBeaconReport from './sendBeaconReport';
import sendReport, { maybeFilterReport } from './sendReport';
import EventEmitter from '../../common/EventEmitter';
import {
  DetectionEvent,
  DetectionReason,
  DetectionType,
  PlayerEvent,
  PrimingEvent,
  ReportingState,
  RuntimeDetectionEvent
} from '../../reporter/constants';
import {
  Events,
  InitializeReporterData,
  InternalEvents,
  PlayerDetectionOrPrimingEventItem,
  ReporterEvent
} from '../../events';
import { Level, LogLevels } from '../../log/LoggerConstants';
import { ActiveDataSources, IReporter, TriggerAction } from './interfaces';
import { ApiEvents, DeliveryMode } from '@kollective-frontend/ksdk-api';
import { NOT_PEERABLE } from '../constants';
import SDKConfig from '../SDKConfig';
import { EventReport } from './events/interfaces';
import {
  isReporting,
  isStartable,
  canTransition,
  transitionAfterSendReport,
  isStartableFromDataRecord,
  couldReport
} from '../../util/reportingStateUtilities';
import { isValidDuration } from './events/durationChangeHandler';
import cleanse from '../../config/util/cleanse';
import bindDiagnosticListeners from '../../api/diagnostics/bindDiagnosticListeners';
import { DiagnosticCode } from '../../api/diagnostics/constants';
import { dispatchSessionDiagnostic } from '../../api/diagnostics/GenerateDiagnostic';
import sendTransferSummaryEvent from '../../util/sendTransferSummaryEvent';

const log = logger.getLogger(__filename);
export const PROBABILITY_EXPEDITE_ON_CRITICAL = 0.05;
export const bothDefinedButDifferent = (a: unknown, b: unknown): boolean => {
  return typeof a !== 'undefined' && typeof b !== 'undefined' && a != b;
};
export const REPORTER_CHECK_INTERVAL = 15000;

export interface ClearStateOptions {
  reportingState?: ReportingState;
  clearStartableInformation?: boolean;
}
export interface PrepareRequestOptions {
  request?: KsdkRequest;
  time?: number;
}
export interface StartOptions {
  restart?: boolean;
  sendBeacon?: boolean;
  sendImmediate?: boolean;
  updateInterval?: boolean;
}

export const warnIfOverwritingContentOrTenantIds = (
  play: PlayState,
  options: InitializeReporterData
): boolean => {
  const { contentId, tenantId } = options;
  const errors: Record<string, unknown> = {};
  if (bothDefinedButDifferent(play.contentId, contentId)) {
    errors.content = { newId: contentId, oldId: play.contentId };
  }
  if (bothDefinedButDifferent(play.tenantId, tenantId)) {
    errors.tenant = { newId: tenantId, oldId: play.tenantId };
  }
  const keys = Object.keys(errors);
  if (keys.length > 0) {
    const tag = keys.join(' and '),
      s = keys.length > 1 ? 's' : '';
    log.warn(`Initializing reporter with different ${tag} id${s}`, errors);
  }
  return keys.length > 0;
};

const checkEquality = (a: unknown, b: unknown): boolean => {
  return JSON.stringify(a) == JSON.stringify(b);
};

const modStatusToString = (
  event: PrimingEvent | RuntimeDetectionEvent
): PrimingEvent | RuntimeDetectionEvent => {
  return Object.entries(event).reduce(
    (item: Record<string, unknown>, [name, value]) => {
      item[name] = name === 'status' ? `${value}` : value;
      return item;
    },
    {}
  ) as unknown as PrimingEvent | RuntimeDetectionEvent;
};

const requestHasConfig = (request: KsdkRequest): boolean => {
  type RSU = Record<string, unknown>;
  return !!((request?.body?.record as RSU)?.general as RSU)?.config;
};

class Reporter implements IReporter {
  static Sections = ReporterSections;
  private oldReports: Record<number, Report>;
  hasTriedToStartReportCount: number;
  eventEmitter: EventEmitter;
  config: ContentConfig;
  localState: LocalState;
  storedInfo: ReporterStoredInfo;
  contentSet: boolean;
  initialReport: Report;
  v?: string;
  merged: Report;
  pending: PendingReport;
  codeToName!: Record<number, string>;
  reportBlock: Record<string, DataReportBlock<unknown>>;
  hasExpeditedOnCritical?: boolean;
  activeData: ActiveDataSources;

  constructor(
    activeData: ActiveDataSources,
    localState: LocalState = new LocalState(),
    defaultAppName = 'ksdk',
    emitter: EventEmitter | null = null
  ) {
    autobind(this);
    this.activeData = activeData;

    this.oldReports = {};
    this.hasTriedToStartReportCount = 0;
    this.eventEmitter = emitter || EventEmitter.getInstance();
    this.config = ContentConfig.getInstance();
    this.localState = localState;
    this.storedInfo = getStoredReportInfo();
    this.contentSet = false;
    this.initialReport = new Report();
    if (!this.config.app) {
      this.config.app = defaultAppName;
    }
    this.reportBlock = {};
    // Setup reporting interval on sdkConfig
    // This happens here because it's reporter related, and config might get fetched after plugin startup
    this.localState.play.sdkConfig.setReportInterval(this.config.report.i);
    this.state.supportsPublish = false;
    this.v = this.localState.play.sdkConfig.version;

    const { browserUserGuid } = this.storedInfo;
    this.state.browserUserGuid = browserUserGuid;
    const { integration } = this.localState.play.sdkConfig;
    logger.addAppender(
      new ReporterAppender(true, log.ERROR, this.eventEmitter)
    );

    this.merged = new Report();
    this.pending = new PendingReport({
      config: this.config.report.max,
      general: {
        v: this.v,
        browserUserGuid,
        integration
      }
    } as PendingReport);
    this.updateReportBlock();

    this.bindEventHandlers();
    bindDiagnosticListeners(this);
    // Run a check on an interval until reporterIsReadyToStart returns true
    this.state.startSetIntervalId = setInterval(() => {
      maybeUpdate11AgentData(this.localState, this.pending).then(() =>
        this.start({ sendImmediate: true })
      );
    }, REPORTER_CHECK_INTERVAL);
  }

  get cleaned(): boolean {
    return this.state.reportingState === ReportingState.CLEANED;
  }

  get sdkConfig(): SDKConfig {
    return this.localState.play.sdkConfig;
  }

  get state(): ReporterState {
    return this.localState.reporter;
  }

  // handle resetting all the data
  restart(): boolean {
    if (!canTransition(this.state, ReportingState.RESTART, true, true)) {
      log.warn('Trying to start from wrong state:', this.state.reportingState);
      return false;
    } else {
      log.info('Restarting the reporter', this.reportingState);
    }
    this.clearState({
      reportingState: ReportingState.PLAY,
      clearStartableInformation: true
    });
    return true;
  }

  async start(opts: StartOptions = {}): Promise<boolean> {
    if (this.state.reportingState === ReportingState.CLEANED) {
      return false;
    }
    if (!hasMinimalRequiredState(this)) {
      log.trace('Does not have minimal required state', {
        contentId: this.localState.play.contentId,
        tenantId: this.localState.play.tenantId,
        sessionToken: !!this.state.sessionToken,
        contentToken: !!this.state.contentToken
      });
      return false;
    }
    const { restart, sendImmediate, updateInterval, sendBeacon } = opts;
    if (sendBeacon) {
      await this._start(false, true, sendBeacon);
      return true;
    }
    if (restart && !this.restart()) {
      return false;
    }
    const isTimedOut = hasTimedOut(this);
    if (updateInterval && (await this.handleUpdateInterval())) {
      return false;
    }
    if (!isStartable(this.state)) {
      if (this.hasTriedToStartReportCount++ < 1) {
        log.debug('Trying to start reporter when already started', this.state);
      }
      return false;
    }
    this.initialReport.ecdn11 = this.mergeEcdn11();
    const isReady = isTimedOut || reporterIsReadyToStart(this);
    if (isReady) {
      const current = this.reportingState;
      log.debug('Starting reporting:', { restart, sendImmediate, current });
      this.clearStartSetInterval();
      await this._start(restart, sendImmediate);
    }
    return isReady;
  }

  mergeEcdn11(): Ecdn11Block {
    return new Ecdn11Block(
      mergedCopy(
        cleanse(this.initialReport.ecdn11 ?? {}),
        cleanse(this.state.ecdn11 ?? {})
      )
    );
  }

  async handleUpdateInterval(): Promise<boolean> {
    if (this.state.reportingState === ReportingState.PLAYING) {
      this._setReportInterval();
      return true;
    } else if (this.state.reportingState === ReportingState.PAUSING) {
      await this.send(DetectionReason.PAUSING, true);
      return true;
    }
    return false;
  }

  clearStartSetInterval(): void {
    if (this.state.startSetIntervalId) {
      clearInterval(this.state.startSetIntervalId);
      this.state.startSetIntervalId = undefined;
    }
  }

  clearReportSetInterval(): void {
    if (this.state.reportIntervalId) {
      clearInterval(this.state.reportIntervalId);
      this.state.reportIntervalId = undefined;
    }
  }

  clearReportSetTimeout(): void {
    if (this.state.reportSetTimeoutId) {
      clearTimeout(this.state.reportSetTimeoutId);
      this.state.reportSetTimeoutId = undefined;
    }
  }

  initialize(options: InitializeReporterData): void {
    const { contentId, tenantId, user, userEmail } = options || {};
    const play = this.localState.play;
    warnIfOverwritingContentOrTenantIds(play, options);
    if (contentId) {
      play.contentId = contentId;
    }
    if (tenantId) {
      play.tenantId = tenantId;
    }
    if (user) {
      this.state.user = user;
    }
    if (userEmail) {
      this.state.userEmail = userEmail;
    }

    this.pending.general.playGuid = this.localState.play.playGuid;
    this.pending.general.start = this.localState.play.start;
    this.pending.general.user = this.state.user;

    play.sdkConfig.setReportInterval(this.config.report.i);
    this.pending.config = this.config.report.max;
    updateReporterWithContent(options || {});

    this.state.hasReporterInitialized = true;
  }

  bindEventHandlers(): void {
    [
      [InternalEvents.TRANSFER_RECORD, 'transfers'],
      [InternalEvents.CONNECTION_RECORD, 'connections'],
      [InternalEvents.PEER_NOTIFICATION, 'peerNotifications'],
      [InternalEvents.ATTACHMENT_REQUEST, 'attachmentRequests'],
      [InternalEvents.NODE_STATE, 'nodeState'],
      [InternalEvents.CONTENT_NOTIFICATION, 'contentNotifications'],
      [InternalEvents.PEER_REQUEST, 'peerRequests'],
      [InternalEvents.DETACH, 'detachments']
    ].forEach(([event, key]) => {
      this.eventEmitter.addEventListener(event as Events, evt => {
        if (!evt) {
          return;
        }
        this.append(key, evt.data);
      });
    });
    this.eventEmitter.addEventListener(InternalEvents.LOG, evt => {
      this.pending.log.append(evt.data);
    });

    const { DETECTION_EVENT, PLAYER_EVENT, PRIMING_EVENT } = InternalEvents;
    [DETECTION_EVENT, PLAYER_EVENT, PRIMING_EVENT].forEach(eventType => {
      this.eventEmitter.addEventListener(eventType, evt => {
        this.addEvent(evt as PlayerDetectionOrPrimingEventItem);
      });
    });

    this.eventEmitter.addEventListener(InternalEvents.NODE_UPDATE, evt => {
      const { node } = evt.data;
      const cluster = node?.attachment?.activeCluster;
      this.state.nodeId = node?.id;
      if (cluster && isValidGuid(cluster.id)) {
        if (this.state.activeClusterId !== cluster.id) {
          log.info('Setting activeClusterId:', cluster.id);
          this.state.activeClusterId = cluster.id;
          if (isStartableFromDataRecord(this.state)) {
            log.info('trying to start from NODE_UPDATE');
            this.start();
          }
        }
      } else {
        log.warn(
          'NODE_UPDATE event does not contain an accurate node payload:',
          node
        );
      }
    });

    this.eventEmitter.addEventListener(
      ApiEvents.PLAYBACK_STATUS_CHANGE,
      evt => {
        const { data } = evt;
        if (!data) {
          return;
        }
        if (data.method === DeliveryMode.NONE) {
          this.state.activeClusterId = NOT_PEERABLE;
        }
      }
    );

    this.eventEmitter.addEventListener(InternalEvents.UPDATE_REPORT, evt => {
      this.handleUpdate(evt.data);
    });

    this.eventEmitter.addEventListener(InternalEvents.UPDATE_STATE, evt => {
      updateLocalState(this.localState, evt.data);
    });

    const { INITIALIZE_REPORTER } = InternalEvents;
    this.eventEmitter.addEventListener(INITIALIZE_REPORTER, evt => {
      this.initialize(evt.data);
    });

    this.eventEmitter.addEventListener(InternalEvents.TRIGGER, evt => {
      if (evt.data.action === TriggerAction.STOP_REPORTER) {
        this.stop();
      }
    });
  }

  timeSinceStart(time?: number): number {
    return timeSinceStart(this.localState, time);
  }

  handleDetectionEvent(evt: DetectionEvent): void {
    const { WEBRTC, ECDN11 } = DetectionType;
    const { type } = evt;
    const event = evt as RuntimeDetectionEvent;
    switch (type) {
      // Only detection events have these types
      case WEBRTC:
      case ECDN11:
        if (event.success === true) {
          log.info('add.detect', type, event.method, event.v);
          saveStoredReportInfo({ type, v: event.v, date: Date.now() });
        }
        if (event.success === false) {
          log.warn('add.detect.failed', type, event.method, event.status);
          const { lastDetection } = this.storedInfo;
          if (lastDetection) {
            event.lastDetection = lastDetection;
          }
        }
        break;
      default:
        // We only do the default here.
        break;
    }
    this.pending.detection.data.push(
      modStatusToString(event) as RuntimeDetectionEvent
    );
  }

  async handlePlayerEvent(event: PlayerEvent): Promise<void> {
    if (!isPlayerEventHandled(event as EventReport, this)) {
      this.state.latestEvent = event as EventReport;
      this.pending.events.data.push(event as EventReport);
    }
    if (log.isEnabled(Level.DEBUG)) {
      const name = this.getCodeName(event);
      log.debug(`PlayerEvent:${name}`, event, this.reportingState);
    }
    await this.updateStateBasedOnEvent(event);
  }

  getCodeName(event: PlayerEvent): string | number | undefined {
    if (!this.codeToName) {
      const events = { ...NonStdPlayerEvents, ...StdPlayerEvents };
      this.codeToName = Object.entries(events).reduce(
        (acc: Record<number, string>, [key, value]) => {
          acc[value.code] = key;
          return acc;
        },
        {}
      );
    }
    return this.codeToName[event.e] || event.e;
  }

  async updateStateBasedOnEvent(event: PlayerEvent): Promise<void> {
    const previous: string = this.reportingState;
    switch (event.e) {
      case StdPlayerEvents.seeking.code:
        if (this.state.reportingState === ReportingState.ENDED) {
          log.info('calling start from seeking');
          this.state.novelData = true;
          await this.start({ restart: true });
        }
        break;
      case StdPlayerEvents.ended.code:
        this.state.novelData = true;
        await this.stop();
        break;
      case StdPlayerEvents.durationchange.code:
        if (isValidDuration((event as EventReport).dur)) {
          this.state.novelData = true;
          this.state.duration = (event as EventReport).dur;
          this.state.hasSeenDuration = true;
          log.debug('calling start from durationchange');
          await this.updateStateToPlay(event, true).then(res => {
            if (!res) {
              this.start({ sendImmediate: true });
            }
          });
        } else {
          log.debug('Invalid durationchange event: skipping', event);
        }
        break;
      case StdPlayerEvents.canplay.code:
      case StdPlayerEvents.canplaythrough.code:
      case StdPlayerEvents.play.code:
      case StdPlayerEvents.playing.code:
        this.state.novelData = true;
        this.updateStateBasedOnPlayEvent(event);
        break;
      case StdPlayerEvents.pause.code:
        if (canTransition(this.state, ReportingState.PAUSING, true, true)) {
          if (!this.state.reportSetTimeoutId) {
            this.addReportTimeout(`PAUSING from ${previous}`);
          }
          const current = this.reportingState;
          this.state.novelData = true;
          log.debug('Transitioned state:', { previous, current });
        }
        break;
      // beforeunload and pagehide are handled in BasePlugin
      case NonStdPlayerEvents.visibilitychange.code:
        if (isReporting(this.state)) {
          this.state.novelData = true;
          await this.send(DetectionReason.VISIBILITY_CHANGE, true, true);
        }
        break;
      // Rest of the events we don't want to do anything special.
    }
  }

  // Returns true if send or start is called.
  async updateStateToPlay(
    event: PlayerEvent,
    sendImmediate?: boolean
  ): Promise<boolean> {
    const { play, playing, durationchange } = StdPlayerEvents;
    const allowed = [play, playing, durationchange].map(c => c.code);
    if (allowed.indexOf(event.e) === -1) {
      return false;
    }
    if (this.state.reportingState === ReportingState.WAITING) {
      this.state.reportingState = ReportingState.PLAY;
      const name = this.getCodeName(event);
      log.info(`Moving from WAITING state because ${name} event now: PLAY`);
      await this.start({ sendImmediate });
      return true;
    }
    return false;
  }

  async updateStateBasedOnTransferRecords(): Promise<void> {
    const count = this.pending?.transfers?.data?.length ?? 0;
    const delta = Date.now() - this.localState.play.start;
    const state = this.state.reportingState;
    const interval = this.sdkConfig.reportInterval;
    if (state !== ReportingState.WAITING || delta < interval || count < 50) {
      return;
    }
    // We have a bunch of transfer records we should be reporting.
    this.pending.detection.data.push({
      reason: DetectionReason.NO_PLAY_EVENT,
      type: DetectionType.PLATFORM
    });
    this.state.reportingState = ReportingState.PLAY;
    log.info(`Moving from WAITING state because we have ${count} transfers`);
    await this.start({});
  }

  async updateStateBasedOnPlayEvent(event: PlayerEvent): Promise<boolean> {
    const { play, playing, canplay, canplaythrough } = StdPlayerEvents;
    const allowed = [play, playing, canplay, canplaythrough].map(c => c.code);
    if (allowed.indexOf(event.e) === -1) {
      return false;
    }
    const isWaiting = this.state.reportingState === ReportingState.WAITING;
    const previous: string = this.reportingState;
    if (isWaiting && (await this.updateStateToPlay(event))) {
      return true;
    }
    if (event.e === play.code || event.e === playing.code) {
      if (canTransition(this.state, ReportingState.PLAY, true)) {
        const current = ReportingState[this.state.reportingState];
        log.debug('Transitioned state:', { previous, current });
      } else {
        log.debug(`unable to transition from ${previous} on a play event`);
      }
    }
    this.state.reporterStartable = true;
    if (!isReporting(this.state) && !isWaiting) {
      log.info('calling start from playing');
      await this.start({ sendImmediate: true });
    }
    return true;
  }

  add(data: PlayerEvent): void {
    this.addEvent({ type: InternalEvents.PLAYER_EVENT, data });
  }

  addEvent(event: PlayerDetectionOrPrimingEventItem): void {
    if (this.cleaned) {
      return;
    }
    //event.data.t = this.timeSinceStart(event.data.t);
    const timeSinceStart = this.timeSinceStart(event.data.t);
    event.data.t = timeSinceStart;

    switch (event.type) {
      case InternalEvents.DETECTION_EVENT:
        this.state.novelData = true;
        this.handleDetectionEvent(event.data);
        break;
      case InternalEvents.PRIMING_EVENT:
        this.state.novelData = true;
        this.pending.priming.data.push(
          modStatusToString(event.data) as PrimingEvent
        );
        break;
      case InternalEvents.PLAYER_EVENT:
        this.handlePlayerEvent(event.data);
        break;
    }
    // Process everything to search for reasons to expedite or flag as critical
    // (Only considers first reasons found for each)
    const { conditionCritical, conditionExpedite } = processReport(
      this.merged,
      this.pending,
      event
    );
    // Prioritize expediting due to a critical condition, if probabilistically chosen as an expedited reporter
    const didExpedite =
      conditionCritical && this.flagCritical(conditionCritical);
    if (!didExpedite && conditionExpedite) {
      this.expedite(conditionExpedite);
    }
    this.updateInterval();
  }

  updateInterval(): void {
    if (this.config.report.i === this.sdkConfig.reportInterval) {
      return;
    }
    this.sdkConfig.setReportInterval(this.config.report.i);
    if (isReporting(this.state)) {
      log.info('calling start from updateInterval');
      this.start({ sendImmediate: true, updateInterval: true });
    }
  }

  updateReportBlock(): void {
    this.reportBlock = {};
    Object.entries(this.pending).forEach(([key, value]) => {
      if (value instanceof DataReportBlock) {
        this.reportBlock[key] = value;
      }
    });
  }

  addReportTimeout(from: string): void {
    if (this.state.reportSetTimeoutId) {
      log.debug('Trying to setTimeout when one is already set up');
      return;
    }
    this.state.reportSetTimeoutId = setTimeout(
      () => this.send(DetectionReason.TIMEOUT_SET, true, false, true),
      this.sdkConfig.reportInterval
    );
    log.debug('addReportTimeout: setting single timeout', {
      from,
      reportingState: this.reportingState
    });
  }

  append(key: string, record: unknown): boolean | undefined {
    if (this.cleaned) {
      return true;
    }
    // Let the websocket know that we are still actively in use.
    if (!this.reportBlock[key]) {
      log.warn(`Trying to append to missing record for ${key}`);
      return false;
    }
    SignalConnection.tick();
    this.reportBlock[key].data.push(record);
    this.state.novelData = true;
    if (key === 'transfers') {
      this.updateStateBasedOnTransferRecords();
    }
    if (isStartableFromDataRecord(this.state)) {
      log.trace(
        `appending ${key} to report record:`,
        record,
        this.reportingState,
        this.state.reportIntervalId
      );
      // DO NOT change from paused...
      log.debug('calling start from append');
      this.start({ sendImmediate: true });
    } else if (couldReport(this.state)) {
      this.addReportTimeout(`adding ${key}`);
    } else {
      log.trace(
        `not trying to start with '${key}' record`,
        this.reportingState,
        this.state.reportIntervalId
      );
    }
    if (!this.state.reportSetTimeoutId && !this.state.reportIntervalId) {
      this.start();
    }
  }

  async handleUpdate(evt: ReporterEvent): Promise<void> {
    if (this.cleaned) {
      return;
    }
    switch (evt.section) {
      case ReporterSections.ECDN11:
        this.handleEcdn11Event(evt.value as Ecdn11Block);
        break;
      case ReporterSections.General: {
        const block = evt.value as MaybeGeneralBlock;
        await this.handleGeneralBlock(block, evt.override);
        break;
      }
    }
  }

  handleEcdn11Event(record: Ecdn11Block): void {
    const obj = mergedCopy(this.state.ecdn11, record) as Ecdn11Block;
    const item = new Ecdn11Block(obj);
    if (!checkEquality(item, this.state.ecdn11)) {
      this.state.novelData = true;
      this.state.ecdn11 = item;
    }
  }

  async handleGeneralBlock(
    record: MaybeGeneralBlock,
    override?: boolean
  ): Promise<void> {
    if (record && record.playback) {
      this.initialReport.general.playback = record.playback;
    }
    if (override) {
      this.pending.general.append(record);
      log.info('calling start from handleGeneralBlock (override)', record);
      await this.start({ sendImmediate: true });
      return;
    }
    // Exclude data keys that are already merged
    // Merge pending.general last so that its existing keys remain
    const novelData = difference(
      cleanse(this.merged.general || {}),
      cleanse(record || {})
    );
    if (novelData && Object.entries(novelData).length != 0) {
      this.state.novelData = true;
      const merged = mergedCopy(novelData, this.pending.general);
      this.pending.general.append(merged);

      // Kick off sending once we have the duration datum
      log.info('calling start from handleGeneralBlock', novelData);
      await this.start({ sendImmediate: true }).then(success => {
        if (!success) {
          this.updateInterval();
        }
      });
    }
  }

  generatePending(): PendingReport {
    const { v } = this;
    const { playback } = this.initialReport.general;
    const { playGuid } = this.localState.play;
    const config = this.config.report.max;
    // appName and locationName are set in formatReportRequest
    const general = { v, playback, playGuid };
    return new PendingReport({ general, config } as PendingReport);
  }

  merge(): void {
    let { merged, pending } = this;
    const errors = [];
    if (!pending) {
      errors.push('pending');
      pending = this.generatePending();
    }
    if (!merged) {
      errors.push('merged');
      merged = new Report();
    }
    if (errors.length > 0) {
      log.warn(`Missing ${errors.join(', ')} when trying to update report`);
    }
    this.merged = mergeReport(merged, pending.toReport(this.config)) as Report;
    this.pending = this.generatePending();
    this.updateReportBlock();
  }

  async send(
    reason: DetectionReason,
    immediate?: boolean,
    useBeacon?: boolean,
    resetOnFailure?: boolean
  ): Promise<void> {
    if (this.cleaned) {
      return;
    }
    if (immediate || useBeacon) {
      // skip checking for agent data when we are doing a final report.
      await this._send(reason, useBeacon, resetOnFailure);
      return;
    }
    await maybeUpdate11AgentData(this.localState, this.pending).then(() => {
      this._send(reason);
    });
  }

  getFilteredInitialReport(): Report {
    const pendingReport = new PendingReport();
    return pendingReport.toReport(this.config, [this.initialReport]);
  }

  formatReportRequest(isBeacon: boolean): FormatReportRequest {
    if (!this.state.initialReportDataSent) {
      this.pending.general.config = ContentConfig.getCopy();
    }

    const len = Object.keys(this.oldReports).length;
    if (this.state.novelData !== true && len == 0) {
      return { statusText: 'no data to send', status: 0 };
    }
    this.pending.general.playGuid = this.localState.play.playGuid;
    this.pending.general.t = this.timeSinceStart();
    this.pending.general.locationName = this.config.networkLocationName;

    const reports = Object.entries(this.oldReports)
      .sort((a, b) => {
        const lt = parseInt(a[0]),
          rt = parseInt(b[0]);
        return lt > rt ? 1 : -1;
      })
      .map(entry => entry[1]);
    setMergedReportIndices(reports, this.pending.general);

    const report = this.pending.toReport(this.config, reports);
    // ActiveClusterIds are picked up from state in formatReportRequest.
    const reportConfig = {
      appName: this.localState.play.sdkConfig.integrationName(),
      contentSet: this.contentSet,
      jwt: this.state.sessionToken || this.state.contentToken,
      report,
      reportingContext: this.config.reportingContext,
      initialReport: this.getFilteredInitialReport(),
      isBeacon,
      state: this.state,
      localState: this.localState,
      headers: this.getHeaders()
    };
    return formatReportRequest(reportConfig);
  }

  prepareRequest(
    isBeacon: boolean,
    resetOnFailure?: boolean
  ): PrepareRequestOptions {
    const request = this.formatReportRequest(isBeacon);
    if (!isaFailedRequest(request)) {
      this.oldReports = {};
      return { request, time: Date.now() };
    }
    // If data is missing to send a report, or if there is no data. We will
    // receive a message instead of a request. The data will be stored for
    // the next attempt to report.
    const level = request.level || LogLevels.INFO;
    const irrevocablyFailed = request.status >= 400;
    const msg = irrevocablyFailed
      ? `report failed: ${request.status} ${request.statusText}`
      : `Not sending report because: ${request.statusText}`;
    log.message(level, msg);
    if (irrevocablyFailed) {
      // If a request has irrevocably failed, disable reporting.
      this.state.isReportingDisabled = true;
      this.cleanup(false, true);
      return {};
    }
    if (resetOnFailure) {
      this.state.reportSetTimeoutId = undefined;
      this.addReportTimeout(`Retry on failure`);
    }
    return {};
  }

  async _send(
    reason: DetectionReason,
    useBeacon = false,
    resetOnFailure = false
  ): Promise<void> {
    if (reason !== DetectionReason.INTERVAL) {
      const msg = { type: DetectionType.REPORTER, reason };
      this.pending.detection.data.push(msg);
    }
    if (!isReporting(this.state)) {
      log.debug('send: Not sending as reporting state is', this.reportingState);
      return;
    }
    const isBeacon = useBeacon && hasSendBeacon();
    const { request, time } = this.prepareRequest(isBeacon, resetOnFailure);
    const report = request?.body?.record as unknown as Report;
    if (!request || !report || !time) {
      return;
    }

    if (report.general) {
      report.general.timestamp = time;
      if (this.config.proxy) {
        report.general.proxyConfig = this.config.proxy;
      }
      if (this.config.networkPolicies) {
        report.general.networkPolicies = this.config.networkPolicies;
      }
    } else {
      log.info('general not set on report', report);
    }
    log.trace('_send: body:', request.body);
    this.merge();
    this.state.novelData = false;
    this.state.reportIndex++;
    this.sendTransferSummaryEvent(request);
    if (isBeacon) {
      // Send beacon only takes a url and body.
      // state transition occurs elsewhere.
      try {
        const didSend = sendBeaconReport(request);
        log.debug(`Beacon report sent with sendBeacon: ${didSend}`);
        if (didSend) {
          this.transitionAfterSendReport();
        } else {
          this.oldReports[time] = report;
          this.state.novelData = true;
        }
      } catch (e) {
        log.error('Failed to sendBeacon for report.', e);
        log.debug('SendBeacon request body:', request.body);
        this.oldReports[time] = report;
        this.state.novelData = true;
      }
    } else {
      if (useBeacon) {
        log.info('_send: useBeacon calling sendReport not sendBeacon');
      }
      // Send report will set this.state.isReportingDisabled on failure.
      await sendReport(this, request, useBeacon)
        .then(async success => {
          log.info('_send: sendReport', success, report);
          if (success) {
            this.transitionAfterSendReport();
          } else {
            await this.handleReportError(time, request);
          }
        })
        .catch(async error => {
          log.warn('Issue sending message:', error);
          await this.handleReportError(
            time,
            request,
            error.message || 'Issue sending report'
          );
        });
    }
  }

  async sendTransferSummaryEvent(request: KsdkRequest): Promise<void> {
    const { localState: state, config } = this;
    const connections = this.activeData.currentPeerConnections();
    sendTransferSummaryEvent({ request, config, connections, state });
  }

  async handleReportError(
    time: number,
    request: KsdkRequest,
    message?: string
  ): Promise<void> {
    this.state.novelData = true;
    if (requestHasConfig(request)) {
      this.pending.general.config = this.config;
    }
    try {
      // Check to see if prepping to send the report threw an exception
      maybeFilterReport(request);
      // Only merge in old reports if they didn't cause an exception.
      this.oldReports[time] = request.body.record as Report;
    } catch (e) {
      const msg = (e as Error)?.message;
      if (message && msg) {
        message = `${message}. Second attempt: ${msg}`;
      } else if (!message) {
        message = msg || 'Error attempting to send report';
      }
    }
    if (!message) {
      return;
    }
    const code = DiagnosticCode.ERROR_SENDING_REPORT;
    const state = this.localState;
    const from = 'sdk-reporter';
    await dispatchSessionDiagnostic({ code, from, message, state });
  }

  clearNovelData(): void {
    this.state.novelData = false;
    this.oldReports = {};
  }

  transitionAfterSendReport(): void {
    this.clearNovelData();
    if (this.state.reportSetTimeoutId) {
      log.debug('Removing reportSetTimeoutId after send', this.reportingState);
      this.state.reportSetTimeoutId = undefined;
    }
    const tstate = transitionAfterSendReport(this.state);
    if (this.state.reportingState === ReportingState.PAUSED) {
      this.clearReportSetInterval();
    }
    if (tstate.current !== tstate.previous) {
      log.debug('Transitioned state:', tstate);
    }
    this.state.initialReportDataSent = true;
  }

  getHeaders(): Record<string, string> {
    const { contentId, playGuid, sdkConfig, tenantId } = this.localState.play;
    const { sessionToken: token } = this.state;
    const args = { contentId, playGuid, sdkConfig, tenantId, token };
    return getKollectiveHeaders(args);
  }

  expedite(condition: string | null): void {
    if (condition) {
      this.pending.general.expedited = condition;
    }
    this.start({ sendImmediate: true });
  }

  flagCritical(condition: null): boolean {
    const shouldExpedite = Math.random() < PROBABILITY_EXPEDITE_ON_CRITICAL;
    this.pending.general.critical = condition;
    if (shouldExpedite && !this.hasExpeditedOnCritical) {
      this.hasExpeditedOnCritical = true;
      this.expedite(CONDITION_EXPEDITE.CRITICAL);
    }
    return shouldExpedite;
  }

  get reportingState(): string {
    return ReportingState[this.state.reportingState];
  }

  async _start(
    restart?: boolean,
    sendImmediate?: boolean,
    useBeacon?: boolean
  ): Promise<void> {
    const hasIntervalId = !!this.state.reportIntervalId;
    if (this.cleaned || (hasIntervalId && !restart)) {
      log.debug(
        `start: immediately returning as either cleaned:` +
          ` ${this.cleaned} or hasIntervalId && !restart` +
          ` (${!this.state.reportIntervalId} && !${!!restart})`
      );
      return;
    }
    if (this.state.reportSetTimeoutId) {
      const states = { current: this.reportingState, next: 'PLAY' };
      log.debug('Removing reportSetTimeoutId in _start', states);
      clearTimeout(this.state.reportSetTimeoutId);
      this.state.reportSetTimeoutId = undefined;
    }
    this.clearState();
    canTransition(this.state, ReportingState.PLAY, true);
    if (isReporting(this.state)) {
      this._setReportInterval();
      if (sendImmediate) {
        await this._send(DetectionReason.START, useBeacon);
      }
    }
  }

  _setReportInterval(): void {
    this.sdkConfig.setReportInterval(this.config.report.i);
    const reportInterval = this.sdkConfig.reportInterval;
    this.state.reportIntervalId = setInterval(
      () => this.send(DetectionReason.INTERVAL),
      reportInterval
    );
  }

  async stop(useBeacon?: boolean, skipSend?: boolean): Promise<void> {
    const shouldSendReport = !skipSend && isReporting(this.state);
    if (!canTransition(this.state, ReportingState.ENDED, false, true)) {
      const reportingState = this.reportingState;
      log.debug('stop: not able to transition to ended from', reportingState);
      return;
    }
    log.debug('stopping');
    if (shouldSendReport) {
      await this._send(DetectionReason.STOP, useBeacon);
    }
    this.clearState({
      reportingState: ReportingState.ENDED,
      clearStartableInformation: true
    });
  }

  clearState(opts?: ClearStateOptions): void {
    opts = opts || {};
    if (typeof opts.reportingState !== 'undefined') {
      const current = ReportingState[opts.reportingState];
      const previous = this.reportingState;
      log.debug('Transitioning state:', { previous, current });
      this.state.reportingState = opts.reportingState;
    }
    this.clearReportSetInterval();
    this.clearStartSetInterval();
    this.clearReportSetTimeout();
    this.hasTriedToStartReportCount = 0;
    this.state.hasReporterTimedOut = false;
    if (opts.clearStartableInformation) {
      this.state.hasSeenDuration = false;
      this.state.reporterStartable = false;
      this.state.initialReportDataSent = false;
    }
  }

  async cleanup(useBeacon?: boolean, skipSend?: boolean): Promise<void> {
    if (this.cleaned) {
      log.debug('Skipping cleanup as it is already done');
      return;
    }
    log.debug('Starting cleanup');
    await this.stop(useBeacon, skipSend);
    this.state.reportingState = ReportingState.CLEANED;
    this.initialReport = null as unknown as Report;
    this.merged = null as unknown as Report;
    this.pending = null as unknown as PendingReport;
    log.debug('Ending cleanup');
  }
}

export default Reporter;
